import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Swords, Loader2, Zap, Shield, Activity, Brain, Trophy, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/errors";

const CYAN = "#00D4FF";
const AMBER = "#FFBF00";
const RED = "#ff4d4d";

function DnaBar({ label, value, icon: Icon }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const color = pct >= 75 ? CYAN : pct >= 50 ? AMBER : "#71717a";
  return (
    <div className="flex items-center gap-2" data-testid={`dna-bar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 w-24 text-[11px] mono text-zinc-400 uppercase tracking-wider">
        <Icon size={10} strokeWidth={1.5} />
        {label}
      </div>
      <div className="flex-1 h-1.5 bg-zinc-900 border border-zinc-800 relative">
        <div
          className="absolute inset-y-0 left-0"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <div className="w-8 mono text-[11px] text-right" style={{ color }}>
        {pct}
      </div>
    </div>
  );
}

function FighterDnaCard({ side, name, dna, winPct }) {
  if (!dna) return null;
  return (
    <div
      className="border border-zinc-800 bg-zinc-950/40 p-4"
      data-testid={`dna-card-${side}`}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="overline text-zinc-500">{side.toUpperCase()} FIGHTER</div>
        <div className="mono text-[11px] text-zinc-500">
          MODEL <span style={{ color: CYAN }}>{winPct?.toFixed?.(1) ?? "—"}%</span>
        </div>
      </div>
      <div className="font-display text-xl text-white leading-tight mb-3 truncate">{name || "TBD"}</div>
      <div className="space-y-2">
        <DnaBar label="Striking" value={dna.striking_score} icon={Zap} />
        <DnaBar label="Grappling" value={dna.grappling_score} icon={Shield} />
        <DnaBar label="Cardio" value={dna.cardio_score} icon={Activity} />
        <DnaBar label="Fight IQ" value={dna.fight_iq_score} icon={Brain} />
      </div>
      {dna.primary_path_to_victory && (
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <div className="overline text-zinc-500 mb-1">PRIMARY PATH</div>
          <div className="text-sm text-zinc-300">{dna.primary_path_to_victory}</div>
        </div>
      )}
    </div>
  );
}

function MovBlock({ label, home, away }) {
  const h = Number(home) || 0;
  const a = Number(away) || 0;
  const total = h + a || 1;
  const hw = (h / total) * 100;
  return (
    <div data-testid={`mov-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="overline text-zinc-500">{label}</div>
        <div className="mono text-[11px] text-zinc-400">
          <span style={{ color: CYAN }}>{h.toFixed(1)}%</span>
          <span className="text-zinc-600 mx-1.5">/</span>
          <span style={{ color: AMBER }}>{a.toFixed(1)}%</span>
        </div>
      </div>
      <div className="h-1.5 bg-zinc-900 border border-zinc-800 flex overflow-hidden">
        <div style={{ width: `${hw}%`, background: CYAN }} />
        <div style={{ width: `${100 - hw}%`, background: AMBER }} />
      </div>
    </div>
  );
}

function RoundBar({ label, pct }) {
  const v = Number(pct) || 0;
  return (
    <div
      className="flex items-center gap-2"
      data-testid={`round-bar-${label.toLowerCase()}`}
    >
      <div className="w-12 mono text-[11px] text-zinc-400 uppercase">{label}</div>
      <div className="flex-1 h-1.5 bg-zinc-900 border border-zinc-800 relative">
        <div className="absolute inset-y-0 left-0 bg-zinc-500" style={{ width: `${Math.min(v, 100)}%` }} />
      </div>
      <div className="w-10 mono text-[11px] text-right text-zinc-300">{v.toFixed(1)}%</div>
    </div>
  );
}

function BoutCard({ bout }) {
  const a = bout.analysis || {};
  const mma = a.mma || {};
  const sim = a.simulation || {};
  const homeName = (bout.home || {}).name || "Home";
  const awayName = (bout.away || {}).name || "Away";
  const mov = mma.method_of_victory || {};
  const rd = mma.round_distribution || {};
  const fp = mma.fight_path || {};
  const jr = mma.judging_risk || {};
  const dna = mma.fighter_dna || {};
  const jrColor = jr.score >= 70 ? RED : jr.score >= 40 ? AMBER : CYAN;

  return (
    <article
      className="border border-zinc-800 bg-zinc-950/40 p-5 space-y-5"
      data-testid={`ufc-bout-${bout.game_id}`}
    >
      {/* Header */}
      <header className="flex items-baseline justify-between gap-3 border-b border-zinc-800 pb-4">
        <div>
          <div className="overline mb-1 text-zinc-500">
            {bout.matchup || `${homeName} vs ${awayName}`}
          </div>
          <div className="font-display text-2xl text-white leading-tight">
            {a.lean || "—"}
          </div>
          <div className="mono text-[11px] text-zinc-500 mt-1">
            {a.verdict || ""}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="overline text-zinc-500">EDGE</div>
          <div
            className="mono text-lg font-bold"
            style={{
              color:
                (a.market_edge || {}).edge_label === "+EV"
                  ? CYAN
                  : (a.market_edge || {}).edge_label === "-EV"
                  ? RED
                  : "#a1a1aa",
            }}
          >
            {((a.market_edge || {}).edge_pct ?? 0).toFixed(1)}%
          </div>
          <div className="mono text-[10px] text-zinc-500 tracking-wider mt-0.5">
            {"★".repeat(Number(a.confidence) || 0) || "—"}
          </div>
        </div>
      </header>

      {/* Fighter DNA cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FighterDnaCard
          side="home"
          name={homeName}
          dna={dna.home}
          winPct={sim.model_home_win_pct}
        />
        <FighterDnaCard
          side="away"
          name={awayName}
          dna={dna.away}
          winPct={sim.model_away_win_pct}
        />
      </section>

      {/* Method-of-Victory */}
      {mov.home && mov.away && (
        <section data-testid="mov-section">
          <div className="overline text-zinc-500 mb-3 flex items-center gap-2">
            <Trophy size={11} strokeWidth={1.5} />
            METHOD OF VICTORY · HOME / AWAY
          </div>
          <div className="space-y-2.5">
            <MovBlock label="KO / TKO" home={mov.home.ko_tko_pct} away={mov.away.ko_tko_pct} />
            <MovBlock label="Submission" home={mov.home.submission_pct} away={mov.away.submission_pct} />
            <MovBlock label="Decision" home={mov.home.decision_pct} away={mov.away.decision_pct} />
          </div>
          {mov.reasoning && (
            <div className="text-sm text-zinc-400 mt-3 leading-relaxed">{mov.reasoning}</div>
          )}
        </section>
      )}

      {/* Round distribution + Judging risk */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {(rd.r1_pct !== undefined || rd.decision_pct !== undefined) && (
          <div data-testid="round-dist-section">
            <div className="overline text-zinc-500 mb-3">ROUND DISTRIBUTION</div>
            <div className="space-y-2">
              <RoundBar label="R1" pct={rd.r1_pct} />
              <RoundBar label="R2" pct={rd.r2_pct} />
              <RoundBar label="R3" pct={rd.r3_pct} />
              {Number(rd.r4_pct) > 0 && <RoundBar label="R4" pct={rd.r4_pct} />}
              {Number(rd.r5_pct) > 0 && <RoundBar label="R5" pct={rd.r5_pct} />}
              <RoundBar label="DEC" pct={rd.decision_pct} />
            </div>
            {rd.reasoning && (
              <div className="text-sm text-zinc-400 mt-3 leading-relaxed">{rd.reasoning}</div>
            )}
          </div>
        )}
        {jr.score !== undefined && (
          <div data-testid="judging-risk-section">
            <div className="overline text-zinc-500 mb-3 flex items-center gap-2">
              <AlertTriangle size={11} strokeWidth={1.5} />
              JUDGING RISK
            </div>
            <div className="flex items-baseline gap-3 mb-3">
              <div
                className="font-display text-5xl leading-none"
                style={{ color: jrColor }}
                data-testid="judging-risk-score"
              >
                {jr.score}
              </div>
              <div className="mono text-[11px] text-zinc-500">/ 100</div>
            </div>
            <div className="h-1.5 bg-zinc-900 border border-zinc-800 mb-3">
              <div
                className="h-full"
                style={{ width: `${Math.min(jr.score, 100)}%`, background: jrColor }}
              />
            </div>
            {jr.reasoning && (
              <div className="text-sm text-zinc-400 leading-relaxed">{jr.reasoning}</div>
            )}
          </div>
        )}
      </section>

      {/* Fight Path */}
      {(fp.most_likely_scenario || fp.spoiler_scenario) && (
        <section data-testid="fight-path-section" className="border-t border-zinc-800 pt-4">
          <div className="overline text-zinc-500 mb-3">FIGHT PATH ANALYZER</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fp.most_likely_scenario && (
              <div>
                <div className="mono text-[10px] mb-1.5" style={{ color: CYAN }}>
                  MOST LIKELY
                </div>
                <div className="text-sm text-zinc-300 leading-relaxed">
                  {fp.most_likely_scenario}
                </div>
              </div>
            )}
            {fp.spoiler_scenario && (
              <div>
                <div className="mono text-[10px] mb-1.5" style={{ color: AMBER }}>
                  SPOILER PATH
                </div>
                <div className="text-sm text-zinc-300 leading-relaxed">
                  {fp.spoiler_scenario}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </article>
  );
}

export default function UfcTerminal() {
  const [bouts, setBouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null); // game_id currently being evaluated
  const [analyzedIds, setAnalyzedIds] = useState(() => new Set());

  const loadBouts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/research/games", { params: { sport: "mma" } });
      setBouts(data.games || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not load UFC card"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBouts();
  }, [loadBouts]);

  const analyzeBout = useCallback(async (game_id) => {
    setAnalyzing(game_id);
    try {
      const { data } = await api.post("/research/evaluate", { sport: "mma", game_id });
      // Merge the analysis payload into the bout record for rendering
      setBouts((prev) =>
        prev.map((b) =>
          String(b.id) === String(game_id)
            ? { ...b, analysis: data.analysis, game_id, matchup: data.game?.matchup || b.name }
            : b,
        ),
      );
      setAnalyzedIds((prev) => new Set(prev).add(String(game_id)));
    } catch (err) {
      toast.error(apiErrorMessage(err, "Analysis failed"));
    } finally {
      setAnalyzing(null);
    }
  }, []);

  const analyzedBouts = bouts.filter((b) => analyzedIds.has(String(b.id)) && b.analysis);
  const remainingBouts = bouts.filter((b) => !analyzedIds.has(String(b.id)));

  return (
    <section data-testid="ufc-terminal">
      <header className="flex items-baseline justify-between border-b border-zinc-800 pb-4 mb-6">
        <div>
          <div className="overline mb-1 flex items-center gap-2" style={{ color: CYAN }}>
            <Swords size={11} strokeWidth={1.5} />
            UFC RESEARCH TERMINAL
          </div>
          <h2 className="font-display text-3xl text-white leading-tight">Fighter DNA · Method · Path.</h2>
          <p className="mono text-[11px] text-zinc-500 mt-2">
            Deep-dive every bout on the card. Method-of-Victory probabilities,
            Round distribution, Fighter DNA, and the AI's fight-path scenario.
          </p>
        </div>
        <button
          onClick={loadBouts}
          data-testid="ufc-refresh-card"
          className="mono text-[11px] text-zinc-500 hover:text-[#00D4FF] tracking-wider"
        >
          RELOAD CARD
        </button>
      </header>

      {loading && (
        <div className="mono text-sm text-zinc-500 py-8 text-center" data-testid="ufc-loading">
          <Loader2 size={14} className="inline animate-spin mr-2" />
          Loading UFC card…
        </div>
      )}

      {!loading && bouts.length === 0 && (
        <div
          className="border border-zinc-800 bg-zinc-950/40 p-8 text-center"
          data-testid="ufc-empty"
        >
          <div className="overline text-zinc-500 mb-2">NO UFC CARD TODAY</div>
          <div className="text-sm text-zinc-400">
            Check back on fight week — the terminal auto-refreshes when ESPN
            posts the next scheduled card.
          </div>
        </div>
      )}

      {/* Analyzed bouts render as full cards */}
      {analyzedBouts.length > 0 && (
        <div className="space-y-6 mb-8" data-testid="ufc-analyzed-list">
          {analyzedBouts.map((b) => (
            <BoutCard key={b.id} bout={b} />
          ))}
        </div>
      )}

      {/* Un-analyzed bouts — click to analyze */}
      {remainingBouts.length > 0 && (
        <div>
          {analyzedBouts.length > 0 && (
            <div className="overline text-zinc-500 mb-3">MORE BOUTS ON THE CARD</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ufc-remaining-list">
            {remainingBouts.map((b) => (
              <div
                key={b.id}
                className="border border-zinc-800 bg-zinc-950/40 p-4 flex items-center justify-between gap-3"
                data-testid={`ufc-bout-row-${b.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="mono text-[10px] text-zinc-500 uppercase tracking-wider truncate">
                    {b.short_name || ""}
                  </div>
                  <div className="text-sm text-white truncate">{b.name || b.matchup || `${(b.home||{}).name} vs ${(b.away||{}).name}`}</div>
                </div>
                <button
                  onClick={() => analyzeBout(b.id)}
                  disabled={analyzing === b.id}
                  data-testid={`ufc-analyze-${b.id}`}
                  className="shrink-0 px-3 py-1.5 mono text-[11px] tracking-wider bg-[#00D4FF] text-black hover:bg-[#00D4FF]/90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {analyzing === b.id ? (
                    <>
                      <Loader2 size={11} className="inline animate-spin mr-1" />
                      RUN
                    </>
                  ) : (
                    "ANALYZE"
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
